const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

document.documentElement.classList.add("has-motion");

let revealObserver;

const prepareReveal = (element) => {
  element.classList.add("reveal");

  if (prefersReducedMotion) {
    element.classList.add("in-view");
  } else if (revealObserver) {
    revealObserver.observe(element);
  }
};

const revealTargets = [
  ".page-hero-inner",
  ".section-heading",
  ".archive-filters",
  ".product-layout",
  ".editorial-card",
  ".instagram-card",
  ".instagram-widget-shell",
  ".product-card",
  ".split-section",
  ".stat-card",
  ".value-card",
  ".press-card",
  ".timeline-card",
].flatMap((selector) => [...document.querySelectorAll(selector)]);

if (prefersReducedMotion) {
  revealTargets.forEach(prepareReveal);
} else {
  revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("in-view");
          revealObserver.unobserve(entry.target);
        }
      });
    },
    {
      rootMargin: "0px 0px -10% 0px",
      threshold: 0.14,
    }
  );

  revealTargets.forEach(prepareReveal);
}

const progress = document.querySelector(".scroll-progress");

if (progress) {
  const updateProgress = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    const amount = scrollable > 0 ? window.scrollY / scrollable : 0;
    progress.style.transform = `scaleX(${Math.min(amount, 1)})`;
  };

  updateProgress();
  window.addEventListener("scroll", updateProgress, { passive: true });
  window.addEventListener("resize", updateProgress);
}

const cursorGlow = document.querySelector(".cursor-glow");

if (cursorGlow && !prefersReducedMotion) {
  window.addEventListener(
    "pointermove",
    (event) => {
      cursorGlow.style.transform = `translate3d(${event.clientX}px, ${event.clientY}px, 0) translate(-50%, -50%)`;
    },
    { passive: true }
  );
}

const motionPanels = [...document.querySelectorAll(".hero, .page-hero")];

motionPanels.forEach((panel) => {
  if (prefersReducedMotion) {
    return;
  }

  panel.addEventListener("pointermove", (event) => {
    const bounds = panel.getBoundingClientRect();
    const x = (event.clientX - bounds.left) / bounds.width - 0.5;
    const y = (event.clientY - bounds.top) / bounds.height - 0.5;
    panel.style.setProperty("--mx", x.toFixed(3));
    panel.style.setProperty("--my", y.toFixed(3));
  });

  panel.addEventListener("pointerleave", () => {
    panel.style.setProperty("--mx", "0");
    panel.style.setProperty("--my", "0");
  });
});

const filterButtons = [...document.querySelectorAll("[data-filter]")];
const filterCards = [...document.querySelectorAll("[data-type]")];
const filterNames = new Set(filterButtons.map((button) => button.dataset.filter));

const applyProductFilter = (filter, updateUrl = false) => {
  if (!filterNames.has(filter)) {
    filter = "all";
  }

  filterButtons.forEach((item) => item.classList.toggle("is-active", item.dataset.filter === filter));
  filterCards.forEach((card) => {
    const isVisible = filter === "all" || card.dataset.type === filter;
    card.classList.toggle("is-hidden", !isVisible);
  });

  if (updateUrl && window.history?.pushState) {
    const url = new URL(window.location.href);

    if (filter === "all") {
      url.searchParams.delete("filter");
    } else {
      url.searchParams.set("filter", filter);
    }

    window.history.pushState({ filter }, "", url);
  }
};

filterButtons.forEach((button) => {
  button.addEventListener("click", (event) => {
    if (button.tagName === "A") {
      event.preventDefault();
    }

    applyProductFilter(button.dataset.filter, true);
  });
});

if (filterButtons.length) {
  const initialFilter = new URLSearchParams(window.location.search).get("filter");
  applyProductFilter(initialFilter || "all");
}

const enableCardLift = (card) => {
  if (prefersReducedMotion) {
    return;
  }

  card.addEventListener("pointermove", (event) => {
    const bounds = card.getBoundingClientRect();
    const x = (event.clientX - bounds.left) / bounds.width - 0.5;
    const y = (event.clientY - bounds.top) / bounds.height - 0.5;
    card.style.transform = `translate(${x * -6}px, ${y * -6}px)`;
  });

  card.addEventListener("pointerleave", () => {
    card.style.transform = "";
  });
};

const liftCards = [...document.querySelectorAll(".editorial-card, .instagram-card, .product-card, .press-card, .value-card")];

liftCards.forEach(enableCardLift);

const instagramCards = document.querySelector("[data-instagram-cards]");
const instagramWidgetShell = document.querySelector(".instagram-widget-shell");
const freeWidgetText = "free instagram feed widget";

const escapeHtml = (value = "") =>
  value
    .toString()
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");

const compactText = (value = "") => value.replace(/\s+/g, " ").trim();

const directText = (node) =>
  compactText(
    [...node.childNodes]
      .filter((child) => child.nodeType === Node.TEXT_NODE)
      .map((child) => child.textContent)
      .join(" ")
  );

const titleFromCaption = (caption = "") => {
  const cleanCaption = compactText(caption);
  return cleanCaption ? cleanCaption.slice(0, 86) : "Latest Instagram post";
};

const imageFromElfsightPost = (post) => {
  const image = post.querySelector("img");

  if (image?.currentSrc || image?.src) {
    return image.currentSrc || image.src;
  }

  const backgroundNode = [...post.querySelectorAll("[style*='background-image']")].find((node) =>
    node.style.backgroundImage.includes("url")
  );

  return backgroundNode?.style.backgroundImage.match(/url\(["']?(.*?)["']?\)/)?.[1] || "";
};

const hideFreeWidgetLabels = () => {
  const possibleLabels = [...document.querySelectorAll("a, div, span")].filter((node) =>
    directText(node).toLowerCase().includes(freeWidgetText)
  );

  possibleLabels.forEach((node) => {
    node.style.setProperty("display", "none", "important");
  });
};

const renderInstagramCards = (posts) => {
  if (!instagramCards || !posts.length) {
    return;
  }

  instagramCards.innerHTML = posts
    .slice(0, 3)
    .map((post, index) => {
      const badge = index === 0 ? '<span class="instagram-badge">Latest post</span>' : "";
      const title = titleFromCaption(post.caption || post.alt);

      return `
        <article class="instagram-card">
          <a href="${escapeHtml(post.url)}" aria-label="View ${escapeHtml(title)} on Instagram">
            <div class="instagram-media">
              <img src="${escapeHtml(post.image)}" alt="${escapeHtml(title)}" />
              ${badge}
            </div>
            <div class="instagram-copy">
              <p class="card-label">@rapstylearcheology</p>
              <h3>${escapeHtml(title)}</h3>
              <span>View on Instagram</span>
            </div>
          </a>
        </article>
      `;
    })
    .join("");

  [...instagramCards.querySelectorAll(".instagram-card")].forEach((card) => {
    prepareReveal(card);
    enableCardLift(card);
  });
};

if (instagramWidgetShell) {
  const syncElfsightPosts = () => {
    hideFreeWidgetLabels();

    const posts = [...instagramWidgetShell.querySelectorAll(".eapps-instagram-feed-posts-item")];
    posts.forEach((post, index) => {
      post.hidden = index >= 3;
    });

    const visiblePosts = posts
      .slice(0, 3)
      .map((post) => ({
        url: post.querySelector("a[href*='instagram.com']")?.href || "https://www.instagram.com/rapstylearcheology",
        image: imageFromElfsightPost(post),
        caption: post.querySelector(".eapps-instagram-feed-posts-item-text")?.textContent || post.textContent,
        alt: post.querySelector("img")?.alt,
      }))
      .filter((post) => post.image);

    renderInstagramCards(visiblePosts);
  };

  syncElfsightPosts();

  new MutationObserver(syncElfsightPosts).observe(instagramWidgetShell, {
    childList: true,
    subtree: true,
  });
}
